import { dayjs } from 'date';
import { forwardRef, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimePicker, type DateTimePickerProps, FormControl, FormHelperText, TextField } from 'ui';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SelectFieldProps = DateTimePickerProps<any> & {
  required?: boolean;
  name: string;
  label: string;
  autocomplete?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  onChange?: (date: string | null) => void;
};

export const DateTimeField = forwardRef<HTMLDivElement, SelectFieldProps>(function DateTimeField(
  {
    label,
    name,
    autocomplete,
    disabled = false,
    required = false,
    helperText,
    size = 'small',
    fullWidth = false,
    format = 'DD/MM/YYYY HH:mm',
    onChange,
    ...rest
  },
  ref
) {
  const { control } = useFormContext();

  const textFieldRef = useRef<HTMLDivElement>(null);

  return (
    <Controller
      control={control}
      name={name}
      rules={
        required
          ? {
              required: 'This field is required',
            }
          : {}
      }
      render={({ field, formState }) => {
        const error = formState.errors[name];
        const { value, ...restField } = field;
        return (
          <FormControl required={required} error={Boolean(error)} size="small" fullWidth={fullWidth}>
            <DateTimePicker
              label={label}
              disabled={disabled}
              {...restField}
              {...rest}
              format={format}
              ref={ref}
              value={value ? dayjs(value) : null}
              fullWidth={fullWidth}
              onChange={(date) => {
                const isoString = date ? date.toISOString() : null;
                field.onChange(isoString);
                if (onChange) {
                  onChange(isoString);
                }
              }}
              name={autocomplete || name}
              slots={{
                textField: (props) => (
                  <TextField
                    ref={textFieldRef}
                    {...props}
                    error={Boolean(error) || props.error}
                    required={required}
                    size={size}
                  />
                ),
              }}
              slotProps={{
                openPickerButton: {
                  // @ts-expect-error - data-testid is a valid prop, despite not being in the type
                  'data-testid': 'date-time-button',
                },
                popper: {
                  // @ts-expect-error ts does not pick up the type of this ref correctly and thinks it can be null, but in reality its always an HTMLElement
                  anchorEl: () => textFieldRef.current,
                },
              }}
            />
            {error || helperText ? (
              <FormHelperText>{error ? String(error?.message) : helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    />
  );
});
